/**
 * Shared bulk-selection scope for Targeting / Search Term / Placement Bids V2.
 *
 * Modes:
 * - selected: explicit page checkboxes → entity IN [ids]
 * - all_matching: current query filters only
 * - all_matching_except: filters + entity NOT IN [excludeIds]
 */

export const BULK_SCOPE = {
  SELECTED: "selected",
  ALL_MATCHING: "all_matching",
  ALL_MATCHING_EXCEPT: "all_matching_except",
};

/** Build a stable row id from one or more field keys (composite-safe). */
export function getRowBulkId(row, idKeys = []) {
  if (!row || !idKeys?.length) return "";
  return idKeys.map((key) => String(row?.[key] ?? "")).join("::");
}

export function getSelectedRowIds(rows = [], idKeys = []) {
  return (rows || [])
    .filter((row) => row?.isChecked)
    .map((row) => getRowBulkId(row, idKeys))
    .filter(Boolean);
}

export function shouldShowSelectAllMatchingBanner({
  scope,
  pageSelectedCount,
  pageSelectableCount,
  totalCount,
}) {
  if (scope === BULK_SCOPE.ALL_MATCHING || scope === BULK_SCOPE.ALL_MATCHING_EXCEPT) {
    return false;
  }
  if (!pageSelectableCount || pageSelectedCount <= 0) return false;
  if (pageSelectedCount < pageSelectableCount) return false;
  return Number(totalCount) > pageSelectedCount;
}

export function getBulkDisplayCount({ scope, selectedIdsCount, totalCount, excludeIdsCount }) {
  const total = Number(totalCount) || 0;
  if (scope === BULK_SCOPE.ALL_MATCHING) return total;
  if (scope === BULK_SCOPE.ALL_MATCHING_EXCEPT) {
    return Math.max(0, total - (Number(excludeIdsCount) || 0));
  }
  return Number(selectedIdsCount) || 0;
}

export function getBulkScopeLabel({ scope, selectedIdsCount, totalCount, excludeIdsCount }) {
  const total = Number(totalCount) || 0;
  const excluded = Number(excludeIdsCount) || 0;
  if (scope === BULK_SCOPE.ALL_MATCHING) {
    return `All ${total.toLocaleString()} matching filters`;
  }
  if (scope === BULK_SCOPE.ALL_MATCHING_EXCEPT) {
    return `All ${total.toLocaleString()} matching filters except ${excluded}`;
  }
  const n = Number(selectedIdsCount) || 0;
  return `${n.toLocaleString()} selected on this page`;
}

/**
 * Apply checkbox toggle under bulk scope rules.
 * Returns { rows, scope, excludeIds }.
 */
export function applyBulkRowToggle({
  rows,
  index,
  scope,
  excludeIds = [],
  idKeys,
  isRowSelectable = () => true,
}) {
  const row = rows?.[index];
  if (!row || !isRowSelectable(row)) {
    return { rows, scope, excludeIds };
  }

  const rowId = getRowBulkId(row, idKeys);
  const nextChecked = !row.isChecked;

  if (scope === BULK_SCOPE.ALL_MATCHING || scope === BULK_SCOPE.ALL_MATCHING_EXCEPT) {
    let nextExclude = [...excludeIds];
    if (!nextChecked) {
      if (rowId && !nextExclude.includes(rowId)) nextExclude.push(rowId);
      const nextScope =
        nextExclude.length > 0
          ? BULK_SCOPE.ALL_MATCHING_EXCEPT
          : BULK_SCOPE.ALL_MATCHING;
      const nextRows = rows.map((r, i) =>
        i === index ? { ...r, isChecked: false } : r
      );
      return { rows: nextRows, scope: nextScope, excludeIds: nextExclude };
    }

    nextExclude = nextExclude.filter((id) => id !== rowId);
    const nextScope =
      nextExclude.length > 0
        ? BULK_SCOPE.ALL_MATCHING_EXCEPT
        : BULK_SCOPE.ALL_MATCHING;
    const nextRows = rows.map((r, i) =>
      i === index ? { ...r, isChecked: true } : r
    );
    return { rows: nextRows, scope: nextScope, excludeIds: nextExclude };
  }

  const nextRows = rows.map((r, i) =>
    i === index ? { ...r, isChecked: nextChecked } : r
  );
  return {
    rows: nextRows,
    scope: BULK_SCOPE.SELECTED,
    excludeIds: [],
  };
}

export function applyBulkSelectAllPage({
  rows,
  checked,
  isRowSelectable = () => true,
}) {
  const nextRows = (rows || []).map((row) => {
    if (!isRowSelectable(row)) return row;
    return { ...row, isChecked: checked };
  });
  return {
    rows: nextRows,
    scope: BULK_SCOPE.SELECTED,
    excludeIds: [],
  };
}

export function applySelectAllMatching({
  rows,
  isRowSelectable = () => true,
}) {
  const nextRows = (rows || []).map((row) => {
    if (!isRowSelectable(row)) return { ...row, isChecked: false };
    return { ...row, isChecked: true };
  });
  return {
    rows: nextRows,
    scope: BULK_SCOPE.ALL_MATCHING,
    excludeIds: [],
  };
}

export function clearBulkSelection(rows) {
  return {
    rows: (rows || []).map((row) => ({ ...row, isChecked: false })),
    scope: BULK_SCOPE.SELECTED,
    excludeIds: [],
  };
}

/** After data refresh in all-matching modes, sync page checkboxes from excludeIds. */
export function syncRowsWithBulkScope({
  rows,
  scope,
  excludeIds = [],
  idKeys,
  isRowSelectable = () => true,
}) {
  if (scope !== BULK_SCOPE.ALL_MATCHING && scope !== BULK_SCOPE.ALL_MATCHING_EXCEPT) {
    return rows;
  }
  const excludeSet = new Set(excludeIds);
  return (rows || []).map((row) => {
    if (!isRowSelectable(row)) return { ...row, isChecked: false };
    const id = getRowBulkId(row, idKeys);
    return { ...row, isChecked: !excludeSet.has(id) };
  });
}
